import { Component, ElementRef, HostListener, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { AuthService } from '../../../core/services/auth/auth.service';
import { normalizeLanguageTag } from '../../../core/utils/language';
import { ProjectContextService } from '@services/project-context/project-context.service';
import { ProjectOption } from '@constants/projects.constants';
import { FilterStateService } from '@services/filter-state/filter-state.service';
import { StructureService } from '@services/structure/structure.service';

type TopMenuSuboption = { text: string; value: string; translate?: boolean };
type TopMenuOption = { text: string; icon?: string; suboptions?: TopMenuSuboption[] };

@Component({
  selector: 'app-top-menu',
  templateUrl: './top-menu.component.html',
  styleUrl: './top-menu.component.scss'
})
export class TopMenuComponent implements OnDestroy {
  private static readonly MENU_PROJECTS_INDEX = 0;
  private static readonly MENU_LANGUAGE_INDEX = 1;
  private static readonly MENU_LOGOUT_INDEX = 2;

  private static readonly MENU_LANGUAGE_SUBOPTIONS: TopMenuSuboption[] = [
    { text: 'es', value: 'es' },
    { text: 'en', value: 'en' },
    { text: 'ca', value: 'ca' },
    { text: 'va', value: 'va' },
    { text: 'ga', value: 'ga' }
  ];

  private readonly subscriptions = new Subscription();

  showMenu: boolean = false;
  subOptionShowing: number | undefined;
  allowedProjects: ProjectOption[] = [];
  MENU_OPTIONS: TopMenuOption[] = [
    {
      text: 'projects',
      icon: 'assets/icons/folder.svg',
      suboptions: []
    },
    {
      text: 'language',
      icon: 'assets/icons/language.svg',
      suboptions: TopMenuComponent.MENU_LANGUAGE_SUBOPTIONS
    },
    {
      text: 'logout',
      icon: 'assets/icons/poweroff.svg'
    }
  ];

  constructor(
    private translocoSrv: TranslocoService,
    private authService: AuthService,
    private elementRef: ElementRef,
    private projectContext: ProjectContextService,
    private filterStateService: FilterStateService,
    private structureService: StructureService,
    private router: Router
  ) {
    this.subscriptions.add(
      this.projectContext.allowedProjects$.subscribe((projects) => {
        this.allowedProjects = projects;
        const option = this.MENU_OPTIONS[TopMenuComponent.MENU_PROJECTS_INDEX];
        if (option && option.text === 'projects') {
          option.suboptions = projects.map((p) => ({ text: p.displayName, value: p.projectId, translate: false }));
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.showMenu = false;
      this.subOptionShowing = undefined;
    }
  }

  toggleMenu() {
    this.showMenu = !this.showMenu;
    this.subOptionShowing = undefined;
  }

  optionClick(optionIndex: number) {
    if(this.subOptionShowing === optionIndex) {
      this.subOptionShowing = undefined;
    } else {
      switch(optionIndex) {
        case TopMenuComponent.MENU_PROJECTS_INDEX: this.subOptionShowing = optionIndex; break;
        case TopMenuComponent.MENU_LANGUAGE_INDEX: this.subOptionShowing = optionIndex; break;
        case TopMenuComponent.MENU_LOGOUT_INDEX: this.logOut(); break;
        default: break;
      }
    }
  }

  subOptionClick(optionIndex: number, value: string) {
    switch(optionIndex) {
      case TopMenuComponent.MENU_PROJECTS_INDEX: this.selectProject(value); break;
      case TopMenuComponent.MENU_LANGUAGE_INDEX: this.selectLanguage(value); break;
      default: break;
    }
  }

  selectLanguage(lang: string) {
    const normalized = normalizeLanguageTag(lang, this.translocoSrv.getDefaultLang());
    if (this.translocoSrv.getActiveLang() === normalized) {
      return;
    }

    // Avoid showing missing keys (e.g. 'date.0') by loading first, then switching.
    this.translocoSrv.load(normalized).subscribe({
      next: () => this.translocoSrv.setActiveLang(normalized),
      error: () => {
        // If we can't load the language, keep the current one.
      }
    });
  }

  selectProject(projectId: string): void {
    this.projectContext.selectProject(projectId);
    this.structureService.clearCache();
    this.filterStateService.loadAvailability(projectId).subscribe({ error: () => {} });
    this.router.navigate(['/home']);
    this.showMenu = false;
    this.subOptionShowing = undefined;
  }

  logOut() {
    this.authService.logout();
  }
}
